import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { type Nil, Text, TextWithTooltip } from '@pbt/pbt-ui-components'

import type {
  RetailOrderLineItem,
  ShippingAddress,
} from '~/api/graphql/generated/types'
import { StatusWarning } from '~/components/elements/Status/Status'
import { useGetChargeSheetItems } from '~/store/duck/clientFinanceData'
import { useIsChewyCheckoutEnabled } from '~/store/hooks/business'
import type { InvoiceLineItem } from '~/types'

import { getIsRetailOrderLineItem } from '../../invoices/invoiceUtils'

const useStyles = makeStyles({
  data: {
    fontSize: '1.4rem',
    fontWeight: 400,
  },
  clientWithAlert: {
    display: 'inline-flex',
    alignItems: 'center',
  },
})

interface ChargesHeaderProps {
  data: string
  isClient: boolean | undefined
  isLoading?: boolean
  isRefundInvoice: boolean
  name: string
  primaryShippingAddress: ShippingAddress | Nil
}

const ChargesHeaderClientInfo = ({
  data,
  isClient,
  isRefundInvoice,
  name,
  primaryShippingAddress,
  isLoading,
}: ChargesHeaderProps) => {
  const classes = useStyles()
  const { t } = useTranslation('Soap')
  const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()
  const chargeSheetSections = useGetChargeSheetItems()
  const chargeSheetContainsROLI = Object.values(chargeSheetSections)
    .flat()
    .some((item: RetailOrderLineItem | InvoiceLineItem) =>
      getIsRetailOrderLineItem(item),
    )

  const showShippingAddress =
    isChewyCheckoutEnabled &&
    !isRefundInvoice &&
    isClient &&
    chargeSheetContainsROLI

  if (isLoading) {
    return null
  }

  if (showShippingAddress && primaryShippingAddress) {
    return (
      <TextWithTooltip
        className={classes.data}
        tooltipText={t('Soap:CHEWY_SHIPPING_ADDRESS_TOOLTIP')}
      >
        {data}
      </TextWithTooltip>
    )
  }

  if (showShippingAddress) {
    return (
      <div className={classes.clientWithAlert}>
        <Text className={classes.data}>{data}</Text>
        <StatusWarning ml="5px">
          {t('Soap:PLEASE_UPDATE_CHEWY_PAYMENT_INFO', { name })}
        </StatusWarning>
      </div>
    )
  }
  return <Text className={classes.data}>{data}</Text>
}

export default ChargesHeaderClientInfo
